package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ProfileViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.*
import com.arena_web.ui.sections.profile.edit.*
import com.arena_web.ui.sections.profile.mobile.ProfileContentMobile
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Page("/profile")
@Composable
fun ProfilePage() {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val userRepo = container.userRepository
    val mobile = isMobile()
    val ctx = rememberPageContext()

    val viewModel = if (userRepo != null) rememberViewModel {
        ProfileViewModel(userRepo, container.teamRepository, container.followRepository, container.cardRepository, container.eventRepository)
    } else null

    var sportName by remember { mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all") }
    val backgroundImage = AppIcons.statisticBackground(sportName)

    AppPageLayout(
        activeTab = SidebarTab.Profile,
        backgroundImage = backgroundImage,
        mobileReady = true,
        onSportChanged = {
            sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
            viewModel?.reloadSportData()
        }
    ) {
        if (viewModel == null) {
            com.arena_web.ui.sections.auth.WelcomeToArenaScreen(
                onSignedIn = { kotlinx.browser.window.location.reload() }
            )
        } else {
            val state by viewModel.uiState.collectAsState()
            var personalInfoOpen by remember { mutableStateOf(false) }
            var focusOpen by remember { mutableStateOf(false) }
            // Mobile "Bring Your Card to Life" sheet + its Create-Photo →
            // avatar-generation hand-off (desktop owns its own copies inside
            // ProfileDesktopContent; these drive the phone bottom-sheet flow).
            var cardManagerOpen by remember { mutableStateOf(false) }
            var avatarGenTemplateId by remember { mutableStateOf<String?>(null) }
            // Non-null when editing an existing card's photo — drives the
            // AvatarGenerationModal's left card to show the generated avatar.
            var avatarEditCard by remember { mutableStateOf<com.arena_web.data.dto.ProfileCardDto?>(null) }

            if (state.isLoading) {
                LoadingSpinner(modifier = Modifier.fillMaxSize(), size = 80.px)
                return@AppPageLayout
            }

            if (mobile) {
                ProfileContentMobile(
                    state = state,
                    cardRepository = container.cardRepository,
                    userPostRepository = container.userPostRepository,
                    onGoalsPeriodChanged = { viewModel.changeGoalsChartPeriod(it) },
                    onAssistsPeriodChanged = { viewModel.changeAssistsChartPeriod(it) },
                    onTeamChartPeriodChanged = { viewModel.changeTeamChartPeriod(it) },
                    onMatchHistoryPageChange = { viewModel.loadMatchHistoryPage(it) },
                    onAvatarConfirmed = { viewModel.loadProfile() },
                    onFollowingClick = { viewModel.openFollowingDialog() },
                    onFollowersClick = { viewModel.openFollowersDialog() },
                    onEditPersonalInfo = { personalInfoOpen = true },
                    onEditFocus = { focusOpen = true },
                    onEditCard = { cardManagerOpen = true },
                )
            } else {
                ProfileDesktopContent(
                    state = state,
                    sportId = state.sportProfile?.sportId ?: "",
                    sportName = sportName,
                    cardRepository = container.cardRepository,
                    loadPosts = container.userPostRepository?.let { repo ->
                        suspend {
                            repo.getMyPosts(mapOf("page" to "1", "size" to "20"))
                                .getOrNull()?.items.orEmpty()
                        }
                    },
                    canCreatePost = container.userPostRepository != null,
                    onGoalsPeriodChanged = { viewModel.changeGoalsChartPeriod(it) },
                    onAssistsPeriodChanged = { viewModel.changeAssistsChartPeriod(it) },
                    onAvatarConfirmed = { viewModel.loadProfile() },
                    onEditPersonalInfo = { personalInfoOpen = true },
                    onEditFocus = { focusOpen = true },
                    onFollowingClick = { viewModel.openFollowingDialog() },
                    onFollowersClick = { viewModel.openFollowersDialog() },
                    onMatchHistoryPageChange = { viewModel.loadMatchHistoryPage(it) },
                    onReorderCards = { ids -> viewModel.reorderProfileCards(ids) },
                    onDeleteCard = { id -> viewModel.deleteProfileCard(id) },
                    onPurchaseRequest = { template -> viewModel.requestPurchase(template) },
                    onPurchaseConfirm = { viewModel.confirmPurchase() },
                    onPurchaseCancel = { viewModel.cancelPurchase() },
                )
            }

            // Following / Followers popups (own profile — with action buttons).
            // The dialog owns its own paging + debounced search; we just hand
            // it a loader that wraps the /follow/me endpoints with the right
            // page/search params.
            val followRepo = container.followRepository
            if (state.showFollowingDialog && followRepo != null) {
                val loadFollowing: suspend (Int, String?) -> Result<com.arena_web.data.dto.FollowUserPageDto> =
                    { page, search ->
                        followRepo.getMyFollowing(buildMap {
                            put("page", "$page")
                            put("size", "20")
                            if (!search.isNullOrBlank()) put("search", search)
                        })
                    }
                if (mobile) {
                    com.arena_web.ui.sections.profile.mobile.MobileFollowListSheet(
                        mode = com.arena_web.ui.components.overlay.FollowListMode.Following,
                        initialCount = state.followingCount ?: 0L,
                        loadPage = loadFollowing,
                        unfollowedInSessionIds = state.unfollowedInSessionIds,
                        deletedFollowerIds = state.deletedFollowerIds,
                        activeActionUserId = state.unfollowConfirmTarget?.userId,
                        onUserClick = { user ->
                            viewModel.closeFollowingDialog()
                            com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, user.userId)
                        },
                        onUnfollowRequest = { viewModel.requestUnfollow(it) },
                        onRefollow = { viewModel.refollowInDialog(it) },
                        onRemoveRequest = { viewModel.requestRemoveFollower(it) },
                        onDiscoverPlayers = {
                            viewModel.closeFollowingDialog()
                            kotlinx.browser.window.location.assign("/people")
                        },
                        onDismiss = { viewModel.closeFollowingDialog() },
                    )
                } else {
                    com.arena_web.ui.components.overlay.FollowListDialog(
                        mode = com.arena_web.ui.components.overlay.FollowListMode.Following,
                        initialCount = state.followingCount ?: 0L,
                        loadPage = loadFollowing,
                        onUserClick = { user ->
                            viewModel.closeFollowingDialog()
                            com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, user.userId)
                        },
                        onDismiss = { viewModel.closeFollowingDialog() }
                    ) { user ->
                        com.arena_web.ui.components.overlay.FollowingActionButton(
                            isStillFollowing = user.userId !in state.unfollowedInSessionIds,
                            onUnfollowRequest = { viewModel.requestUnfollow(user) },
                            onRefollow = { viewModel.refollowInDialog(user) }
                        )
                    }
                }
            }
            if (state.showFollowersDialog && followRepo != null) {
                val loadFollowers: suspend (Int, String?) -> Result<com.arena_web.data.dto.FollowUserPageDto> =
                    { page, search ->
                        followRepo.getMyFollowers(buildMap {
                            put("page", "$page")
                            put("size", "20")
                            if (!search.isNullOrBlank()) put("search", search)
                        })
                    }
                if (mobile) {
                    com.arena_web.ui.sections.profile.mobile.MobileFollowListSheet(
                        mode = com.arena_web.ui.components.overlay.FollowListMode.Followers,
                        initialCount = state.followersCount ?: 0L,
                        loadPage = loadFollowers,
                        unfollowedInSessionIds = state.unfollowedInSessionIds,
                        deletedFollowerIds = state.deletedFollowerIds,
                        activeActionUserId = state.removeFollowerConfirmTarget?.userId,
                        onUserClick = { user ->
                            viewModel.closeFollowersDialog()
                            com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, user.userId)
                        },
                        onUnfollowRequest = { viewModel.requestUnfollow(it) },
                        onRefollow = { viewModel.refollowInDialog(it) },
                        onRemoveRequest = { viewModel.requestRemoveFollower(it) },
                        onDiscoverPlayers = {
                            viewModel.closeFollowersDialog()
                            kotlinx.browser.window.location.assign("/people")
                        },
                        onDismiss = { viewModel.closeFollowersDialog() },
                    )
                } else {
                    com.arena_web.ui.components.overlay.FollowListDialog(
                        mode = com.arena_web.ui.components.overlay.FollowListMode.Followers,
                        initialCount = state.followersCount ?: 0L,
                        loadPage = loadFollowers,
                        onUserClick = { user ->
                            viewModel.closeFollowersDialog()
                            com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, user.userId)
                        },
                        onDismiss = { viewModel.closeFollowersDialog() }
                    ) { user ->
                        com.arena_web.ui.components.overlay.RemoveFollowerActionButton(
                            isDeleted = user.userId in state.deletedFollowerIds,
                            onRemoveRequest = { viewModel.requestRemoveFollower(user) }
                        )
                    }
                }
            }
            state.unfollowConfirmTarget?.let { target ->
                if (mobile) {
                    com.arena_web.ui.sections.profile.mobile.MobileFollowConfirmSheet(
                        user = target,
                        title = t.pageUnfollowTitle(target.fullName?.takeIf { it.isNotBlank() } ?: "@${target.username}"),
                        confirmLabel = t.feedUnfollow,
                        onConfirm = { viewModel.confirmUnfollow() },
                        onCancel = { viewModel.cancelUnfollow() }
                    )
                } else {
                    com.arena_web.ui.components.overlay.FollowConfirmDialog(
                        user = target,
                        title = t.pageUnfollowTitle(target.fullName?.takeIf { it.isNotBlank() } ?: "@${target.username}"),
                        confirmLabel = t.feedUnfollow,
                        onConfirm = { viewModel.confirmUnfollow() },
                        onCancel = { viewModel.cancelUnfollow() }
                    )
                }
            }
            state.removeFollowerConfirmTarget?.let { target ->
                if (mobile) {
                    com.arena_web.ui.sections.profile.mobile.MobileFollowConfirmSheet(
                        user = target,
                        title = t.pageRemoveFollowerTitle,
                        subtitle = t.pageRemoveFollowerSubtitle(target.fullName?.takeIf { it.isNotBlank() } ?: target.username),
                        confirmLabel = t.feedRemove,
                        onConfirm = { viewModel.confirmRemoveFollower() },
                        onCancel = { viewModel.cancelRemoveFollower() }
                    )
                } else {
                    com.arena_web.ui.components.overlay.FollowConfirmDialog(
                        user = target,
                        title = t.pageRemoveFollowerTitle,
                        subtitle = t.pageRemoveFollowerSubtitle(target.fullName?.takeIf { it.isNotBlank() } ?: target.username),
                        confirmLabel = t.feedRemove,
                        onConfirm = { viewModel.confirmRemoveFollower() },
                        onCancel = { viewModel.cancelRemoveFollower() }
                    )
                }
            }

            PersonalInfoEditModal(
                isOpen = personalInfoOpen,
                initial = personalInfoInitial(state),
                onClose = { personalInfoOpen = false },
                onSave = { form ->
                    viewModel.updatePersonalInfo(
                        fullName = listOf(form.firstName, form.lastName)
                            .filter { it.isNotBlank() }
                            .joinToString(" "),
                        dateOfBirthDisplay = form.dateOfBirth,
                        city = form.city,
                        bio = form.bio,
                        languages = form.languages
                    )
                    personalInfoOpen = false
                }
            )

            FocusActivitiesEditModal(
                isOpen = focusOpen,
                initial = FocusActivitiesFormState(
                    sports = state.interestSports,
                    activities = state.interestActivities,
                    lifestyle = state.interestLifestyle,
                    notes = state.interestNotes
                ),
                onClose = { focusOpen = false },
                onSave = { form ->
                    viewModel.updateInterests(
                        sports = form.sports,
                        activities = form.activities,
                        lifestyle = form.lifestyle,
                        notes = form.notes
                    )
                    focusOpen = false
                }
            )

            // Mobile "Bring Your Card to Life" bottom-sheet flow (Figma
            // 8171:174894 / 8171:174820). Desktop wires its equivalent inside
            // ProfileDesktopContent; this branch is the phone counterpart and
            // reuses the same card-manager + purchase + avatar-generation
            // modals, opened from the card pencil in ProfileContentMobile.
            val cardRepo = container.cardRepository
            if (mobile && cardRepo != null) {
                if (cardManagerOpen) {
                    com.arena_web.ui.components.overlay.BringCardToLifePopup(
                        isOpen = true,
                        onClose = { cardManagerOpen = false },
                        ownedTemplates = state.ownedTemplates,
                        profileCards = state.profileCards,
                        sportName = sportName,
                        activeTemplateId = state.user?.mainProfileCard?.userCardId,
                        onCreatePhoto = { templateId -> avatarEditCard = null; avatarGenTemplateId = templateId },
                        onSaveOrder = { ids -> viewModel.reorderProfileCards(ids) },
                        onLockedClick = { template -> viewModel.requestPurchase(template) },
                        onEditCard = { card -> avatarEditCard = card; avatarGenTemplateId = card.userCardId },
                        onDeleteCardPhoto = { card -> viewModel.deleteProfileCard(card.id) },
                        mobile = true,
                    )
                }

                com.arena_web.ui.components.overlay.PurchaseCardModal(
                    card = state.pendingPurchase,
                    sportName = sportName,
                    purchasing = state.purchasing,
                    purchaseError = state.purchaseError,
                    onConfirm = { _ -> viewModel.confirmPurchase() },
                    onDismiss = { viewModel.cancelPurchase() },
                )

                val genId = avatarGenTemplateId
                if (genId != null) {
                    val template = state.ownedTemplates.firstOrNull { it.id == genId }
                    if (template != null) {
                        com.arena_web.ui.components.overlay.AvatarGenerationModal(
                            isOpen = true,
                            onClose = { avatarGenTemplateId = null; avatarEditCard = null },
                            cardRepository = cardRepo,
                            cardId = template.id,
                            mainProfileCard = avatarEditCard,
                            fallbackCard = template,
                            sportName = sportName,
                            onAvatarConfirmed = {
                                avatarGenTemplateId = null
                                avatarEditCard = null
                                viewModel.loadProfile()
                            },
                            editProfileCardId = avatarEditCard?.id,
                        )
                    }
                }
            }
        }
    }
}

private fun personalInfoInitial(state: com.arena_web.core.viewmodel.ProfileUiState): PersonalInfoFormState {
    val full = state.user?.fullName.orEmpty().trim()
    val (first, last) = if (full.isEmpty()) "" to "" else {
        val parts = full.split(" ", limit = 2)
        parts[0] to (parts.getOrNull(1).orEmpty())
    }
    return PersonalInfoFormState(
        firstName = first,
        lastName = last,
        dateOfBirth = isoToDisplay(state.user?.dateOfBirth),
        city = state.user?.city.orEmpty(),
        languages = state.languages,
        bio = state.user?.bio.orEmpty()
    )
}

/** "YYYY-MM-DD" → "DD.MM.YYYY". Returns "" if not parseable. */
private fun isoToDisplay(iso: String?): String {
    if (iso.isNullOrBlank()) return ""
    val datePart = iso.substringBefore('T').trim()
    val parts = datePart.split('-')
    if (parts.size != 3) return ""
    val (y, m, d) = parts
    if (y.length != 4 || m.length !in 1..2 || d.length !in 1..2) return ""
    return "${d.padStart(2, '0')}.${m.padStart(2, '0')}.$y"
}

@Composable
private fun ProfileDesktopContent(
    state: com.arena_web.core.viewmodel.ProfileUiState,
    sportId: String,
    sportName: String,
    cardRepository: com.arena_web.domain.repositories.CardRepository? = null,
    loadPosts: (suspend () -> List<com.arena_web.data.dto.UserPostDto>)? = null,
    canCreatePost: Boolean = false,
    onGoalsPeriodChanged: (String) -> Unit,
    onAssistsPeriodChanged: (String) -> Unit,
    onAvatarConfirmed: () -> Unit = {},
    onEditPersonalInfo: () -> Unit = {},
    onEditFocus: () -> Unit = {},
    onFollowingClick: () -> Unit = {},
    onFollowersClick: () -> Unit = {},
    onMatchHistoryPageChange: (Int) -> Unit = {},
    onReorderCards: (List<String>) -> Unit = {},
    onDeleteCard: (String) -> Unit = {},
    onPurchaseRequest: (com.arena_web.data.dto.UserCardDto) -> Unit = {},
    onPurchaseConfirm: () -> Unit = {},
    onPurchaseCancel: () -> Unit = {},
) {
    // Read-only preference-details dialog target — hoisted out of OverviewSidebar
    // because the sidebar's root Column has `backdrop-filter: blur(...)`, which
    // establishes a containing block for `position: fixed` children. Rendering
    // the dialog from this scope (no blurred ancestor) lets it cover the whole
    // viewport instead of being clipped to the sidebar bounds.
    var viewNoteTarget by remember { mutableStateOf<String?>(null) }
    // "Bring Your Card to Life" popup state — opened from the carousel sync icon.
    var cardManagerOpen by remember { mutableStateOf(false) }
    // When the user picks a template + presses "Create Photo" in the manager,
    // we close the manager and open the existing AvatarGenerationModal for that
    // template id.
    var avatarGenTemplateId by remember { mutableStateOf<String?>(null) }
    // Non-null when editing an existing card's photo (Edit action) — drives the
    // AvatarGenerationModal's left card to show the generated avatar.
    var avatarEditCard by remember { mutableStateOf<com.arena_web.data.dto.ProfileCardDto?>(null) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .sPadding(26, 40)
    ) {
        // 3-column layout per Figma (600 / 561 / 639)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .sGap(8)
                .styleModifier { property("align-items", "stretch") }
        ) {
            // Left column: Overview sidebar
            OverviewSidebar(
                user = state.user,
                sportProfile = state.sportProfile,
                aggregateRating = state.aggregateRating,
                followersCount = state.followersCount,
                followingCount = state.followingCount,
                isOwnProfile = true,
                onEditPersonalInfo = onEditPersonalInfo,
                onEditFocus = onEditFocus,
                onFollowingClick = onFollowingClick,
                onFollowersClick = onFollowersClick,
                onSyncCardsClick = { cardManagerOpen = true },
                onEditCardClick = { cardManagerOpen = true },
                profileCards = state.profileCards,
                languages = state.languages,
                interestSports = state.interestSports,
                interestActivities = state.interestActivities,
                interestLifestyle = state.interestLifestyle,
                interestNotes = state.interestNotes,
                onInterestNoteClick = { id -> viewNoteTarget = id },
                modifier = Modifier.styleModifier { property("flex", "600 1 0") }
            )

            // Center column: empty top (stadium bg), Recent Events at bottom
            Column(
                modifier = Modifier
                    .fillMaxHeight()
                    .sPaddingLeft(16)
                    .styleModifier {
                        property("flex", "561 1 0")
                        property("justify-content", "flex-end")
                    }
            ) {
                RecentMatchesSection(
                    matches = state.matchHistory,
                    currentPage = state.matchHistoryPage,
                    totalPages = matchHistoryTotalPages(state.matchHistoryTotal),
                    onPageChange = onMatchHistoryPageChange,
                    isLoading = state.matchHistoryLoading,
                )
            }

            // Right column: Player Hub
            PlayerHubSection(
                sportName = sportName,
                sportId = sportId,
                isOwnProfile = true,
                cardRepository = cardRepository,
                loadPosts = loadPosts,
                canCreatePost = canCreatePost,
                activeCardId = state.user?.mainProfileCard?.userCardId,
                onActiveCardChanged = { onAvatarConfirmed() },
                statsCharts = state.statsCharts,
                goalsChartPeriod = state.goalsChartPeriod,
                assistsChartPeriod = state.assistsChartPeriod,
                onGoalsPeriodChanged = onGoalsPeriodChanged,
                onAssistsPeriodChanged = onAssistsPeriodChanged,
                userName = state.user?.fullName ?: state.user?.username ?: "",
                userAvatarUrl = state.user?.avatarUrl,
                sportProfile = state.sportProfile,
                modifier = Modifier.styleModifier { property("flex", "639 1 0") }
            )
        }
    }

    // Read-only preference-details dialog (Figma 7844:341021) — rendered at this
    // scope so `position: fixed` covers the full viewport. See note where
    // viewNoteTarget is declared.
    val target = viewNoteTarget
    val targetNote = target?.let { state.interestNotes[it] }.orEmpty()
    if (target != null && targetNote.isNotBlank()) {
        com.arena_web.ui.components.overlay.DetailsPreferenceDialog(
            option = com.arena_web.ui.sections.profile.edit.InterestCatalog.lookup(target),
            note = targetNote,
            onDismiss = { viewNoteTarget = null }
        )
    }

    // "Bring Your Card to Life" — owned-template picker + profile-card gallery
    // reorder. Rendered here (not inside the blurred sidebar) so the modal
    // overlay covers the whole viewport.
    if (cardManagerOpen && cardRepository != null) {
        com.arena_web.ui.components.overlay.BringCardToLifePopup(
            isOpen = true,
            onClose = { cardManagerOpen = false },
            ownedTemplates = state.ownedTemplates,
            profileCards = state.profileCards,
            sportName = sportName,
            activeTemplateId = state.user?.mainProfileCard?.userCardId,
            onCreatePhoto = { templateId ->
                // Keep the parent popup mounted underneath — AvatarGenerationModal
                // layers on top via its own fixed overlay (higher z-index).
                avatarEditCard = null
                avatarGenTemplateId = templateId
            },
            onSaveOrder = { ids -> onReorderCards(ids) },
            onLockedClick = { template -> onPurchaseRequest(template) },
            onEditCard = { card -> avatarEditCard = card; avatarGenTemplateId = card.userCardId },
            onDeleteCardPhoto = { card -> onDeleteCard(card.id) },
        )
    }

    // Purchase-confirm dialog — fired when a locked tile in the BringCardToLife
    // popup is clicked. Lives at this scope so its overlay covers the whole
    // viewport (parent sidebar has backdrop-filter).
    com.arena_web.ui.components.overlay.PurchaseCardModal(
        card = state.pendingPurchase,
        sportName = sportName,
        purchasing = state.purchasing,
        purchaseError = state.purchaseError,
        onConfirm = { _ -> onPurchaseConfirm() },
        onDismiss = { onPurchaseCancel() },
    )

    // Avatar generation modal — opened by the "Create Photo" CTA inside the
    // manager popup. We synthesize a UserCardDto for the chosen template from
    // the ownedTemplates list (the modal needs the template visuals).
    //
    // `mainProfileCard` is left null on purpose: this flow always *creates a
    // new* gallery card (ConfirmCardAvatarUseCase appends a fresh ProfileCard
    // on confirm). Even when the user already has a card with the same
    // template, the new instance must start from an empty silhouette — passing
    // the existing card's avatar made the popup look like an edit-in-place and
    // bled the first card's avatar into the second card's creation.
    val genId = avatarGenTemplateId
    if (genId != null && cardRepository != null) {
        val template = state.ownedTemplates.firstOrNull { it.id == genId }
        if (template != null) {
            com.arena_web.ui.components.overlay.AvatarGenerationModal(
                isOpen = true,
                onClose = { avatarGenTemplateId = null; avatarEditCard = null },
                cardRepository = cardRepository,
                cardId = template.id,
                mainProfileCard = avatarEditCard,
                fallbackCard = template,
                sportName = sportName,
                onAvatarConfirmed = {
                    avatarGenTemplateId = null
                    avatarEditCard = null
                    onAvatarConfirmed()
                },
                editProfileCardId = avatarEditCard?.id,
            )
        }
    }
}
